import styled, { css } from "styled-components"

import { NavLink } from "components/common/NavLink"

export const MENU_CLOSED_WIDTH = "64px"
export const MENU_OPEN_WIDTH = "230px"
const TRANSITION_DURATION = "0.25s"
const TRANSITION_EASING = "cubic-bezier(0.4, 0, 0.2, 1)"
const MOBILE_TRANSITION = "0.1s"
const MOBILE_BREAKPOINT = "768px"

export const MOBILE_NAV_Z_INDEX_OVERLAY = 5000
export const MOBILE_NAV_Z_INDEX_CONTENT = MOBILE_NAV_Z_INDEX_OVERLAY + 1

export const MobileOverlay = styled.div<{ $isOpen: boolean }>`
  display: none;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: ${MOBILE_NAV_Z_INDEX_OVERLAY};
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(10px);
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    pointer-events: ${({ $isOpen }) => ($isOpen ? "auto" : "none")};
    transition: all ${MOBILE_TRANSITION} ease-in-out;
  }
`

export const StyledNav = styled.nav<{ $isOpen: boolean }>`
  position: fixed;
  left: 0;
  top: 64px;
  height: calc(100vh - 64px);
  background: transparent;
  z-index: 1999;
  display: flex;
  flex-direction: column;
  padding: 0 16px;
  width: ${({ $isOpen }) => ($isOpen ? MENU_OPEN_WIDTH : MENU_CLOSED_WIDTH)};
  transition:
    width ${TRANSITION_DURATION} ${TRANSITION_EASING},
    transform ${TRANSITION_DURATION} ${TRANSITION_EASING};
  overflow: hidden;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    top: 0;
    height: 100vh;
    background: linear-gradient(90deg, #627afa 3.88%, #4ae9ff 94.88%);
    width: 0;
    padding: 64px 0 0;
    transition: all ${MOBILE_TRANSITION} ease-in-out;
    transform: none;
    z-index: ${MOBILE_NAV_Z_INDEX_CONTENT};

    ${({ $isOpen }) =>
      $isOpen &&
      css`
        width: 265px;
        padding: 64px 16px 0;
      `}
  }
`

export const MenuItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const MenuIcon = styled.span`
  height: 24px;
  flex-shrink: 0;
  transition: fill 0.2s ease;

  svg {
    height: 24px;
    fill: #fff;
    transition: fill 0.2s ease;

    path {
      fill: inherit;
    }
  }
`

export const MenuIconStroke = styled(MenuIcon)`
  svg {
    fill: none;
    stroke: #fff;
    transition: stroke 0.2s ease;
  }
`

export const MenuText = styled.span<{ $isOpen: boolean }>`
  margin-left: 8px;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity ${TRANSITION_DURATION} ${TRANSITION_EASING};
`

export const RedDot = styled.span<{ $isOpen: boolean }>`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  border-radius: 9px;
  background: rgb(215, 61, 61);
  font-size: 10px;
  color: #fff;
  flex-shrink: 0;
  transition: all ${TRANSITION_DURATION} ${TRANSITION_EASING};

  ${({ $isOpen }) =>
    $isOpen
      ? css`
          min-width: 18px;
          height: 18px;
          padding: 0 4px;
          margin-left: auto;
          position: static;
        `
      : css`
          position: absolute;
          top: 4px;
          left: 26px;
          min-width: 14px;
          height: 14px;
          padding: 0 3px;
          font-size: 9px;
        `}
`

const menuItemStyles = css`
  display: flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 24px;
  font-family: "Inter";
  font-size: 0.875rem;
  font-weight: 500;
  color: #fff;
  transition: all 0.2s ease;
  white-space: nowrap;
  text-decoration: none;
  min-height: 36px;
  min-width: 46px;
  position: relative;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`

const getActiveStyles = (activeIconColor?: string) => {
  const color = activeIconColor || "#52c2fd"
  return css`
    background: #fff;
    color: ${color};

    ${MenuIcon} svg {
      fill: ${color};
    }

    ${MenuIconStroke} svg {
      fill: none;
      path {
        stroke: ${color};
      }
    }
  `
}

export const StyledNavLink = styled(NavLink)<{ $activeIconColor?: string }>`
  ${menuItemStyles}

  &.active {
    ${({ $activeIconColor }) => getActiveStyles($activeIconColor)}
  }
`

export const BetaBadge = styled.span<{ $isOpen: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  color: #f59e0b;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  flex-shrink: 0;
  transition: all ${TRANSITION_DURATION} ${TRANSITION_EASING};
`

export const MenuLabelWrapper = styled.div<{ $isOpen: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 8px;
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity ${TRANSITION_DURATION} ${TRANSITION_EASING};
  min-width: 0;

  span {
    margin-left: 0;
  }
`

export const MenuGroupHeader = styled.div`
  ${menuItemStyles}
  cursor: pointer;
`

export const MenuChevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-left: 4px;
  /* Default icon points down (expanded state); flip to up when collapsed. */
  transform: rotate(${({ $expanded }) => ($expanded ? "0deg" : "180deg")});
  transition: transform ${TRANSITION_DURATION} ${TRANSITION_EASING};

  svg {
    width: 20px;
    height: 20px;
  }

  /* icon-down.svg ships with a hard-coded slate fill; force it to inherit. */
  svg path {
    fill: currentColor;
  }
`

export const MenuSubItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const SubNavLink = styled(NavLink)`
  ${menuItemStyles}
  padding-left: 32px;

  &.active {
    ${getActiveStyles()}
  }
`

export const CollapsedGroupTrigger = styled.div<{ $active: boolean }>`
  ${menuItemStyles}
  justify-content: center;
  cursor: pointer;

  /* The popover is the hover affordance — suppress the translucent-white
     hover background inherited from menuItemStyles so the icon doesn't
     "light up" on hover (active rows keep their solid background). */
  &:hover {
    background: ${({ $active }) => ($active ? "#fff" : "transparent")};
  }

  ${({ $active }) => $active && getActiveStyles()}
`

export const PopoverDropdown = styled.div`
  position: fixed;
  z-index: 6000;
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 180px;
  padding: 8px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 16px 32px -12px rgba(15, 23, 42, 0.16);
`

export const PopoverItem = styled(NavLink)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  text-decoration: none;
  font-family: "Inter";
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
  white-space: nowrap;
  transition: background 0.15s ease;

  &:hover {
    background: #f1f5f9;
  }

  .popover-check {
    visibility: hidden;
  }

  &.active .popover-check {
    visibility: visible;
  }
`

export const PopoverCheck = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`
